"use client";

import { useRef, useState, ChangeEvent, FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupTextarea,
  InputGroupText,
} from "@/components/ui/input-group";

const categories = [
  "General",
  "Technical",
  "Employment",
  "Site issue",
  "Legal / Privacy",
] as const;

const MAX_MESSAGE_LENGTH = 1000;

export default function Page() {
  const [status, setStatus] = useState({
    submitted: false,
    submitting: false,
    info: { error: false, msg: null as string | null },
  });

  const categoryTriggerRef = useRef<HTMLButtonElement>(null);

  const [inputs, setInputs] = useState({
    name: "",
    email: "",
    subject: "",
    category: "",
    message: "",
  });

  const handleServerResponse = (ok: boolean, msg: string) => {
    if (ok) {
      setStatus({
        submitted: true,
        submitting: false,
        info: { error: false, msg },
      });
      setInputs({
        name: "",
        email: "",
        subject: "",
        category: "",
        message: "",
      });
    } else {
      setStatus({
        submitted: false,
        submitting: false,
        info: { error: true, msg },
      });
    }
  };

  const handleOnChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { id, value } = e.target;

    // Enforce message length limit
    if (id === "message" && value.length > MAX_MESSAGE_LENGTH) {
      return;
    }

    setInputs((prev) => ({
      ...prev,
      [id]: value,
    }));

    setStatus({
      submitted: false,
      submitting: false,
      info: { error: false, msg: null },
    });
  };

  const handleCategoryChange = (value: string) => {
    setInputs((prev) => ({
      ...prev,
      category: value,
    }));

    setStatus({
      submitted: false,
      submitting: false,
      info: { error: false, msg: null },
    });
  };

  const handleOnSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Native validation covers the text fields. The Radix Select is checked here instead:
    // its hidden native <select required> blocks submit without being focusable.
    if (!inputs.category) {
      setStatus({
        submitted: false,
        submitting: false,
        info: { error: true, msg: "Please select a category." },
      });
      categoryTriggerRef.current?.focus();
      return;
    }

    setStatus((prevStatus) => ({ ...prevStatus, submitting: true }));

    fetch("https://formspree.io/f/xvgeyvvr", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(inputs),
    })
      .then(async (response) => {
        if (response.ok) {
          handleServerResponse(
            true,
            "Thank you for your message! I'll get back to you soon.",
          );
        } else {
          const data = await response.json().catch(() => null);
          const errorMsg =
            data?.error || "Something went wrong. Please try again later.";
          handleServerResponse(false, errorMsg);
        }
      })
      .catch(() => {
        handleServerResponse(
          false,
          "Something went wrong. Please try again later.",
        );
      });
  };

  return (
    <div className="max-w-2xl mx-auto py-8">
      <PageHeader
        title="Get in Touch"
        description="Have a question or want to work together? Send me a message."
        className="mb-8"
      />

      <Card className="p-6">
        <form onSubmit={handleOnSubmit} className="space-y-6" aria-label="Contact form">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="Your name"
                value={inputs.name}
                onChange={handleOnChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                Email <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={inputs.email}
                onChange={handleOnChange}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">
              Subject <span className="text-destructive" aria-hidden="true">*</span>
            </Label>
            <Input
              id="subject"
              type="text"
              placeholder="What's this about?"
              value={inputs.subject}
              onChange={handleOnChange}
              required
            />
          </div>

          <div className="space-y-2">
            <Label id="category-label">
              Category <span className="text-destructive" aria-hidden="true">*</span>
            </Label>
            <Select
              value={inputs.category}
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger
                ref={categoryTriggerRef}
                aria-labelledby="category-label"
                aria-required="true"
                className="w-full"
              >
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">
              Message <span className="text-destructive" aria-hidden="true">*</span>
            </Label>
            <InputGroup>
              <InputGroupTextarea
                id="message"
                placeholder="Tell me more about your inquiry..."
                value={inputs.message}
                onChange={handleOnChange}
                className="min-h-32"
                maxLength={MAX_MESSAGE_LENGTH}
                aria-describedby="message-count"
                required
              />
              <InputGroupAddon align="block-end">
                <InputGroupText id="message-count" className="ml-auto">
                  {inputs.message.length}/{MAX_MESSAGE_LENGTH}
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          </div>

          {status.info.error && (
            <div role="alert" className="p-4 rounded-md bg-destructive/10 text-destructive border border-destructive/30">
              Error: {status.info.msg}
            </div>
          )}

          {!status.info.error && status.info.msg && (
            <div role="status" className="p-4 rounded-md bg-primary/10 text-primary border border-primary/30">
              {status.info.msg}
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            data-faro-user-action-name="contact-form-submit"
            disabled={status.submitting}
          >
            {!status.submitting
              ? !status.submitted
                ? "Send Message"
                : "Message Sent"
              : "Sending..."}
          </Button>
        </form>
      </Card>

      <Card className="p-6 mt-6">
        <h2 className="text-lg font-semibold mb-2">Alternate Contact Methods</h2>
        <p className="text-muted-foreground text-sm mb-4">
          You can also contact me via the following:
        </p>
        <div className="flex flex-col gap-2 text-sm text-muted-foreground">
          <div>
            <span className="mr-2">Email:</span>
            <a
              href="mailto:alex@malott.dev"
              data-faro-user-action-name="contact-email"
              className="hover:text-foreground transition-colors"
            >
              alex@malott.dev
            </a>
            <a
              href="/pgp-key.asc"
              download
              data-faro-user-action-name="contact-pgp-key"
              className="hover:text-foreground transition-colors ml-2"
            >
              (PGP Key)
            </a>
          </div>
          <div>
            <span className="mr-2">Phone:</span>
            <a
              href="tel:+17203464293"
              data-faro-user-action-name="contact-phone"
              className="hover:text-foreground transition-colors"
            >
              +1-720-346-4293
            </a>
          </div>
          <div>
            <span className="mr-2">LinkedIn:</span>
            <a
              href="https://www.linkedin.com/in/alex-malott/"
              data-faro-user-action-name="contact-linkedin"
              className="hover:text-foreground transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              linkedin.com/in/alex-malott
            </a>
          </div>
          <div>
            <span className="mr-2">GitLab:</span>
            <a
              href="https://gitlab.com/amalott"
              data-faro-user-action-name="contact-gitlab"
              className="hover:text-foreground transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              gitlab.com/amalott
            </a>
          </div>
        </div>
      </Card>
    </div>
  );
}
